/**
 * User access — who may open EQ Practice, EQ Accounts and EQ Payroll. Managers only.
 *
 * SUITE-DECISIONS.md R14 and R18. The page shows and changes the roster; it
 * decides nothing itself. The lockout rules (nobody changes their own access,
 * in any product; the practice always keeps a manager) are the server's,
 * checked inside the transaction, and their messages are shown as they come
 * back. Your own row is shown read-only only so the reason is visible before
 * you try.
 *
 * R18: a tick for EQ Accounts or EQ Payroll lets the person open that product
 * and nothing more. Which clients or companies they see, and what they may do
 * there, is given inside that product. Only the products this practice has
 * are shown (the server says which).
 */

import { useCallback, useEffect, useState } from 'react'
import {
  api,
  type AccessProduct,
  type AccessRole,
  type AuthState,
  type ObligationTypeInUse,
  type OfferedProduct,
  type PersonAccess,
  type UserAccessOverview,
} from '../api'
import { AppShell } from '../components/AppShell'
import { ErrorState, LoadingState } from '../components/States'

const ROLE_WORDS: Record<AccessRole, string> = { staff: 'Staff', manager: 'Manager' }

/** "No Practice access" is a real choice now: someone may hold Accounts or Payroll only. */
type PracticeChoice = AccessRole | 'none'

const date = (iso: string | null): string =>
  iso === null ? '—' : new Date(iso).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
const dateTime = (iso: string): string =>
  new Date(iso).toLocaleString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })

const sectionCls = 'mb-4 rounded-eq-md border border-eq-border bg-white px-5 py-4'
const h2Cls = 'mb-2 font-heading text-[11px] font-bold uppercase tracking-[.08em] text-eq-neutral'
const thCls = 'px-2 py-1 text-left font-semibold'
const tdCls = 'px-2 py-1.5 align-top'
const inputCls = 'rounded-eq-sm border border-eq-border px-2 py-1 text-[13.5px]'
const primaryBtn = (disabled: boolean) =>
  [
    'rounded-eq-sm px-3 py-1.5 font-heading text-[12.5px] font-bold',
    disabled ? 'cursor-not-allowed bg-eq-grey text-eq-helper' : 'bg-eq-navy text-white',
  ].join(' ')
const quietBtn = 'rounded-eq-sm border border-eq-navy px-3 py-1 font-heading text-[12px] font-bold text-eq-navy'
const dangerBtn = 'rounded-eq-sm border border-eq-danger px-3 py-1 font-heading text-[12px] font-bold text-eq-danger'

const otherProducts = (offered: OfferedProduct[]) => offered.filter((p) => p.id !== 'practice')

function invitationWords(product: AccessProduct, role: string, offered: OfferedProduct[]): string {
  if (product === 'practice') return `EQ Practice — ${ROLE_WORDS[role as AccessRole] ?? role}`
  return offered.find((p) => p.id === product)?.label ?? product
}

function PracticeSelect({
  value,
  onChange,
  disabled,
  label,
  allowNone,
}: {
  value: PracticeChoice
  onChange: (r: PracticeChoice) => void
  disabled?: boolean
  label: string
  allowNone: boolean
}) {
  return (
    <select
      aria-label={label}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as PracticeChoice)}
      className={inputCls}
    >
      {allowNone && <option value="none">No access</option>}
      <option value="staff">Staff</option>
      <option value="manager">Manager</option>
    </select>
  )
}

/** The Practice role plus a tick for each other product -- used to invite and to give access. */
function ProductPicker({
  offered,
  practice,
  setPractice,
  ticks,
  setTicks,
  who,
}: {
  offered: OfferedProduct[]
  practice: PracticeChoice
  setPractice: (r: PracticeChoice) => void
  ticks: Partial<Record<AccessProduct, boolean>>
  setTicks: (t: Partial<Record<AccessProduct, boolean>>) => void
  who: string
}) {
  return (
    <span className="flex flex-wrap items-center gap-3">
      <label className="flex flex-col text-[12px] text-eq-neutral">
        EQ Practice
        <PracticeSelect value={practice} onChange={setPractice} label={`EQ Practice role for ${who}`} allowNone />
      </label>
      {otherProducts(offered).map((p) => (
        <label key={p.id} className="flex items-center gap-1.5 pt-4 text-[13px]">
          <input
            type="checkbox"
            checked={ticks[p.id] === true}
            onChange={(e) => setTicks({ ...ticks, [p.id]: e.target.checked })}
            aria-label={`${p.label} for ${who}`}
          />
          {p.label}
        </label>
      ))}
    </span>
  )
}

function grantsFrom(practice: PracticeChoice, ticks: Partial<Record<AccessProduct, boolean>>) {
  const grants: Partial<Record<AccessProduct, string>> = {}
  if (practice !== 'none') grants.practice = practice
  for (const [product, on] of Object.entries(ticks) as [AccessProduct, boolean][]) {
    if (on && product !== 'practice') grants[product] = 'user'
  }
  return grants
}

function describeGrants(grants: Partial<Record<AccessProduct, string>>, offered: OfferedProduct[]): string {
  return (Object.entries(grants) as [AccessProduct, string][])
    .map(([product, role]) => invitationWords(product, role, offered))
    .join(', ')
}

export function UserAccessPage({
  obligationTypes,
  auth,
}: {
  obligationTypes: ObligationTypeInUse[]
  auth: AuthState
}) {
  const [data, setData] = useState<UserAccessOverview | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      setData(await api.userAccess())
      setLoadError(null)
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e))
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  /** Every change: run it, show the server's words on failure, reload either way. */
  const act: Act = async (fn, done) => {
    if (busy) return
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const result = await fn()
      setNotice(typeof done === 'function' ? done(result) : done)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      await load()
      setBusy(false)
    }
  }

  const active = data?.people.filter((p) => p.status === 'active') ?? []
  const requesting = data?.people.filter((p) => p.status === 'requesting') ?? []
  const former = data?.people.filter((p) => p.status === 'revoked') ?? []
  const offered = data?.products ?? []
  const names = offered.map((p) => p.label).join(', ').replace(/, ([^,]*)$/, ' and $1')

  return (
    <AppShell
      obligationTypes={obligationTypes}
      auth={auth}
      eyebrow="Practice"
      title="User access"
      subtitle={`Who may open ${names || 'EQ Practice'}. Every change is kept, with who made it and when.`}
    >
      {auth.mode === 'anonymous' ? (
        <section className={sectionCls}>
          <p className="m-0 text-[14px]">
            This server is running without sign-in (local development), so there are no users to manage and everyone is
            let in. User access is managed on the live site.
          </p>
        </section>
      ) : loadError !== null ? (
        <ErrorState message={loadError} />
      ) : data === null ? (
        <LoadingState what="user access" />
      ) : (
        <>
          {error !== null && (
            <div className="mb-4">
              <ErrorState message={error} />
            </div>
          )}
          {notice !== null && (
            <p role="status" className="mb-4 rounded-eq-md border border-eq-success bg-eq-success-bg px-4 py-2 text-[13.5px] text-eq-success">
              {notice}
            </p>
          )}

          <InviteSection busy={busy} days={data.invitationDays} offered={offered} act={act} />

          {offered.length > 1 && (
            <p className="mb-4 text-[12.5px] text-eq-helper">
              A tick for {otherProducts(offered).map((p) => p.label).join(' or ')} lets the person open it. Which clients
              or companies they see there, and what they may do, is given inside that product.
            </p>
          )}

          {requesting.length > 0 && (
            <section className={sectionCls}>
              <h2 className={h2Cls}>Asking for access ({requesting.length})</h2>
              <p className="mb-3 text-[13px] text-eq-neutral">
                These people signed in but have not been given access yet. Their sign-in is checked by Cognito and this
                practice; that is all it proves — give access only to people you expect.
              </p>
              <table className="w-full border-collapse text-[13.5px]">
                <thead>
                  <tr className="border-b border-eq-border text-eq-neutral">
                    <th className={thCls}>Person</th>
                    <th className={`${thCls} w-[9rem]`}>First signed in</th>
                    <th className={thCls}>Give access</th>
                  </tr>
                </thead>
                <tbody>
                  {requesting.map((p) => (
                    <GrantRow key={p.userId} person={p} offered={offered} busy={busy} act={act} verb="Give access" />
                  ))}
                </tbody>
              </table>
            </section>
          )}

          <section className={sectionCls}>
            <h2 className={h2Cls}>People with access ({active.length})</h2>
            <table className="w-full border-collapse text-[13.5px]">
              <thead>
                <tr className="border-b border-eq-border text-eq-neutral">
                  <th className={thCls}>Person</th>
                  <th className={`${thCls} w-[8rem]`} title="The initials SmartPractice uses for this person. Imported work tagged with them is allocated to them.">
                    Initials
                  </th>
                  <th className={`${thCls} w-[14rem]`}>EQ Practice</th>
                  {otherProducts(offered).map((p) => (
                    <th key={p.id} className={`${thCls} w-[9rem]`}>
                      {p.label}
                    </th>
                  ))}
                  <th className={`${thCls} w-[8rem]`}>Last seen</th>
                  <th className={`${thCls} w-[14rem]`} />
                </tr>
              </thead>
              <tbody>
                {active.map((p) => (
                  <ActiveRow key={p.userId} person={p} offered={offered} you={p.userId === data.you} busy={busy} act={act} />
                ))}
              </tbody>
            </table>
          </section>

          {data.invitations.length > 0 && (
            <section className={sectionCls}>
              <h2 className={h2Cls}>Invitations not yet taken up ({data.invitations.length})</h2>
              <p className="mb-3 text-[13px] text-eq-neutral">
                Every invitation is taken up the first time the person signs in to EQ Practice — including invitations to
                other products.
              </p>
              <table className="w-full border-collapse text-[13.5px]">
                <thead>
                  <tr className="border-b border-eq-border text-eq-neutral">
                    <th className={thCls}>E-mail</th>
                    <th className={`${thCls} w-[12rem]`}>For</th>
                    <th className={`${thCls} w-[12rem]`}>Invited by</th>
                    <th className={`${thCls} w-[10rem]`}>Expires</th>
                    <th className={`${thCls} w-[8rem]`} />
                  </tr>
                </thead>
                <tbody>
                  {data.invitations.map((i) => (
                    <tr key={i.id} className="border-b border-eq-border last:border-0">
                      <td className={tdCls}>{i.email}</td>
                      <td className={tdCls}>{invitationWords(i.product, i.role, offered)}</td>
                      <td className={tdCls}>
                        {i.invitedBy ?? '—'}
                        <div className="text-[12px] text-eq-helper">{date(i.invitedAt)}</div>
                      </td>
                      <td className={tdCls}>
                        {i.expired ? (
                          <span className="rounded-eq-pill bg-eq-neutral-bg px-2 py-0.5 font-heading text-[11px] font-bold uppercase tracking-[.06em] text-eq-neutral">
                            Expired
                          </span>
                        ) : (
                          date(i.expiresAt)
                        )}
                      </td>
                      <td className={`${tdCls} text-right`}>
                        <button
                          type="button"
                          className={quietBtn}
                          disabled={busy}
                          onClick={() => void act(() => api.cancelInvitation(i.id), `Invitation to ${i.email} cancelled.`)}
                        >
                          Cancel
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {former.length > 0 && (
            <section className={sectionCls}>
              <h2 className={h2Cls}>Former access ({former.length})</h2>
              <table className="w-full border-collapse text-[13.5px]">
                <thead>
                  <tr className="border-b border-eq-border text-eq-neutral">
                    <th className={thCls}>Person</th>
                    <th className={`${thCls} w-[9rem]`}>Access ended</th>
                    <th className={thCls}>Restore</th>
                  </tr>
                </thead>
                <tbody>
                  {former.map((p) => (
                    <GrantRow key={p.userId} person={p} offered={offered} busy={busy} act={act} verb="Restore" />
                  ))}
                </tbody>
              </table>
            </section>
          )}

          <section className={sectionCls}>
            <h2 className={h2Cls}>History</h2>
            {data.history.length === 0 ? (
              <p className="m-0 text-[13px] text-eq-helper">Nothing yet.</p>
            ) : (
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="border-b border-eq-border text-eq-neutral">
                    <th className={`${thCls} w-[11rem]`}>When</th>
                    <th className={thCls}>Person</th>
                    <th className={thCls}>What</th>
                    <th className={`${thCls} w-[10rem]`}>By</th>
                  </tr>
                </thead>
                <tbody>
                  {data.history.map((e, n) => (
                    <tr key={`${e.at}-${n}`} className="border-b border-eq-border last:border-0">
                      <td className={`${tdCls} tabular-nums text-eq-neutral`}>{dateTime(e.at)}</td>
                      <td className={tdCls}>
                        {e.name}
                        {e.name !== e.email && <div className="text-[12px] text-eq-helper">{e.email}</div>}
                      </td>
                      <td className={tdCls}>
                        {e.what}
                        {e.note !== null && <div className="text-[12px] text-eq-helper">{e.note}</div>}
                      </td>
                      <td className={tdCls}>{e.by ?? <span className="text-eq-helper">outside the app</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </AppShell>
  )
}

type Act = <T>(fn: () => Promise<T>, done: string | ((result: T) => string)) => Promise<void>

function PersonCell({ person }: { person: PersonAccess }) {
  return (
    <td className={tdCls}>
      <div className="font-semibold">{person.name}</div>
      {person.name !== person.email && <div className="text-[12px] text-eq-helper">{person.email}</div>}
    </td>
  )
}

function InviteSection({
  busy,
  days,
  offered,
  act,
}: {
  busy: boolean
  days: number
  offered: OfferedProduct[]
  act: Act
}) {
  const [email, setEmail] = useState('')
  const [practice, setPractice] = useState<PracticeChoice>('staff')
  const [ticks, setTicks] = useState<Partial<Record<AccessProduct, boolean>>>({})
  const [note, setNote] = useState('')
  const grants = grantsFrom(practice, ticks)
  const ready = email.trim().includes('@') && Object.keys(grants).length > 0 && !busy

  const submit = () => {
    const address = email.trim()
    const what = describeGrants(grants, offered)
    void act(
      async () => {
        const result = await api.inviteUser(address, grants, note.trim() || undefined)
        setEmail('')
        setNote('')
        setTicks({})
        setPractice('staff')
        if (result.outcome === 'already_has_access') {
          throw new Error(`${address} already has that access. Change it under People with access.`)
        }
        return result
      },
      (result) =>
        result.outcome === 'granted'
          ? `${address} had already signed in, so they have access now: ${what}.`
          : `${address} is invited: ${what}. Access starts the first time they sign in to EQ Practice.`
    )
  }

  return (
    <section className={sectionCls}>
      <h2 className={h2Cls}>Invite someone</h2>
      <p className="mb-3 text-[13px] text-eq-neutral">
        They get access the first time they sign in to EQ Practice with this address, if Cognito has verified it and they
        sign in within {days} days. That first sign-in also takes up any invitation to another product. They need a sign-in
        for this practice first — ask your administrator to create one.
      </p>
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault()
          if (ready) submit()
        }}
      >
        <label className="flex flex-col text-[12px] text-eq-neutral">
          E-mail address
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`${inputCls} w-[18rem]`}
            placeholder="name@thfs.co.za"
          />
        </label>
        <ProductPicker
          offered={offered}
          practice={practice}
          setPractice={setPractice}
          ticks={ticks}
          setTicks={setTicks}
          who="the invitation"
        />
        <label className="flex flex-col text-[12px] text-eq-neutral">
          Note (optional)
          <input value={note} onChange={(e) => setNote(e.target.value)} className={`${inputCls} w-[16rem]`} />
        </label>
        <button type="submit" disabled={!ready} className={primaryBtn(!ready)}>
          Invite
        </button>
      </form>
    </section>
  )
}

function GrantRow({
  person,
  offered,
  busy,
  act,
  verb,
}: {
  person: PersonAccess
  offered: OfferedProduct[]
  busy: boolean
  act: Act
  verb: string
}) {
  const [practice, setPractice] = useState<PracticeChoice>('staff')
  const [ticks, setTicks] = useState<Partial<Record<AccessProduct, boolean>>>({})
  const grants = grantsFrom(practice, ticks)
  const ready = Object.keys(grants).length > 0 && !busy
  return (
    <tr className="border-b border-eq-border last:border-0">
      <PersonCell person={person} />
      <td className={`${tdCls} text-eq-neutral`}>
        {person.status === 'revoked' ? (
          <>
            {date(person.revokedAt)}
            {person.revokedBy !== null && <div className="text-[12px] text-eq-helper">by {person.revokedBy}</div>}
          </>
        ) : (
          date(person.createdDate)
        )}
      </td>
      <td className={tdCls}>
        <span className="flex flex-wrap items-end gap-3">
          <ProductPicker
            offered={offered}
            practice={practice}
            setPractice={setPractice}
            ticks={ticks}
            setTicks={setTicks}
            who={person.name}
          />
          <button
            type="button"
            className={primaryBtn(!ready)}
            disabled={!ready}
            onClick={() =>
              void act(
                () => api.inviteUser(person.email, grants),
                `${person.name} now has: ${describeGrants(grants, offered)}.`
              )
            }
          >
            {verb}
          </button>
        </span>
      </td>
    </tr>
  )
}

/**
 * D3: the initials SmartPractice tags this person's work with (TH, ST ...).
 * Recording them here allocates every imported obligation carrying them to
 * this person, until a manager allocates it explicitly. Not an access change,
 * so it is not covered by "nobody changes their own access".
 */
function InitialsEditor({ person, busy, act }: { person: PersonAccess; busy: boolean; act: Act }) {
  const [value, setValue] = useState(person.initials ?? '')
  useEffect(() => setValue(person.initials ?? ''), [person.initials])
  const changed = value.trim().toUpperCase() !== (person.initials ?? '')
  return (
    <span className="flex items-center gap-1.5">
      <input
        className="w-16 rounded-eq-sm border border-eq-border px-2 py-1 font-heading text-[13px] uppercase"
        value={value}
        maxLength={4}
        aria-label={`Initials for ${person.name}`}
        onChange={(e) => setValue(e.target.value.replace(/[^a-zA-Z]/g, ''))}
      />
      {changed && (
        <button
          type="button"
          className={primaryBtn(busy)}
          disabled={busy}
          onClick={() =>
            void act(
              () => api.setInitials(person.userId, value.trim() === '' ? null : value.trim()),
              value.trim() === '' ? `${person.name}'s initials cleared.` : `${person.name} is ${value.trim().toUpperCase()}.`
            )
          }
        >
          Save
        </button>
      )}
    </span>
  )
}

function ActiveRow({
  person,
  offered,
  you,
  busy,
  act,
}: {
  person: PersonAccess
  offered: OfferedProduct[]
  you: boolean
  busy: boolean
  act: Act
}) {
  const current: PracticeChoice = person.role ?? 'none'
  const [practice, setPractice] = useState<PracticeChoice>(current)
  const [ending, setEnding] = useState(false)
  const [note, setNote] = useState('')

  useEffect(() => setPractice(current), [current])

  const savePractice = () =>
    void act(
      () =>
        practice === 'none'
          ? api.revokeUserAccess(person.userId, undefined, 'practice')
          : api.setUserAccess(person.userId, practice, undefined, 'practice'),
      practice === 'none'
        ? `${person.name} can no longer open EQ Practice.`
        : `${person.name} is now ${ROLE_WORDS[practice]} in EQ Practice.`
    )

  return (
    <tr className="border-b border-eq-border last:border-0">
      <PersonCell person={person} />
      <td className={tdCls}>
        <InitialsEditor person={person} busy={busy} act={act} />
      </td>
      <td className={tdCls}>
        {you ? (
          <>
            {current === 'none' ? 'No access' : ROLE_WORDS[current]}
            <div className="text-[12px] text-eq-helper">This is you. Another manager changes your access.</div>
          </>
        ) : (
          <>
            <span className="flex items-center gap-2">
              <PracticeSelect
                value={practice}
                onChange={setPractice}
                disabled={busy}
                label={`EQ Practice role for ${person.name}`}
                allowNone
              />
              {practice !== current && (
                <button type="button" className={primaryBtn(busy)} disabled={busy} onClick={savePractice}>
                  Save
                </button>
              )}
            </span>
            {person.grantedBy !== null && (
              <div className="text-[12px] text-eq-helper">
                by {person.grantedBy}, {date(person.grantedAt)}
              </div>
            )}
          </>
        )}
      </td>
      {otherProducts(offered).map((p) => {
        const held = person.products[p.id]
        return (
          <td key={p.id} className={tdCls}>
            {you ? (
              held !== undefined ? 'Yes' : '—'
            ) : (
              <label className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={held !== undefined}
                  disabled={busy}
                  aria-label={`${p.label} for ${person.name}`}
                  onChange={(e) =>
                    void act(
                      () =>
                        e.target.checked
                          ? api.setUserAccess(person.userId, 'user', undefined, p.id)
                          : api.revokeUserAccess(person.userId, undefined, p.id),
                      e.target.checked
                        ? `${person.name} can now open ${p.label}. Give them clients there.`
                        : `${person.name} can no longer open ${p.label}. It takes effect on their next click.`
                    )
                  }
                />
                {held !== undefined ? 'Yes' : 'No'}
              </label>
            )}
            {held !== undefined && <div className="text-[12px] text-eq-helper">{date(held.grantedAt)}</div>}
          </td>
        )
      })}
      <td className={`${tdCls} text-eq-neutral`}>{date(person.lastSeenAt)}</td>
      <td className={`${tdCls} text-right`}>
        {!you &&
          (ending ? (
            <span className="flex flex-col items-end gap-1">
              <input
                aria-label={`Why ${person.name}'s access is ending`}
                placeholder="Reason (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className={`${inputCls} w-[14rem]`}
              />
              <span className="flex gap-2">
                <button type="button" className={quietBtn} onClick={() => setEnding(false)}>
                  Keep
                </button>
                <button
                  type="button"
                  className={dangerBtn}
                  disabled={busy}
                  onClick={() =>
                    void act(
                      () => api.revokeUserAccess(person.userId, note.trim() || undefined, 'all'),
                      `${person.name} can no longer open any Equilibrium product here. It takes effect on their next click.`
                    )
                  }
                >
                  End all access
                </button>
              </span>
            </span>
          ) : (
            <button type="button" className={dangerBtn} disabled={busy} onClick={() => setEnding(true)}>
              End all access
            </button>
          ))}
      </td>
    </tr>
  )
}
